import { useCallback, useEffect, useRef, useState } from 'react'

export const DROPDOWN_OPEN_EVENT = 'backoffice:dropdown-open'

const cache: Record<string, any> = {}

interface DropdownOpenDetail {
    scope?: string
}

export const notifyDropdownOpen = (scope?: string) => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent<DropdownOpenDetail>(DROPDOWN_OPEN_EVENT, {
        detail: scope ? { scope } : undefined,
    }))
}

interface UseCachedFetchOptions {
    refreshOnDropdownOpen?: boolean | string
    reportRefreshErrors?: boolean
}

interface UseCachedFetchResult<T> {
    data: T | undefined
    isLoading: boolean
    hasError: boolean
    refetch: () => void
    silentRefetch?: (shouldApply?: (result: T) => boolean) => Promise<T | undefined>
}

/**
 * Fetches data for a given cache key.
 * @param cacheKey - unique key (e.g. "entities:acme"). Pass null to skip fetching.
 * @param fetcher - async function that returns data
 */
export function useCachedFetch<T>(
    cacheKey: string | null,
    fetcher: () => Promise<T>,
    options: UseCachedFetchOptions = {},
): UseCachedFetchResult<T> {
    const [data, setData] = useState<T | undefined>(cacheKey ? cache[cacheKey] as T | undefined : undefined)
    const [isLoading, setIsLoading] = useState(!!cacheKey && cache[cacheKey] === undefined)
    const [hasError, setHasError] = useState(false)
    const fetchIdRef = useRef(0)
    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    useEffect(() => {
        if (!cacheKey) {
            setData(undefined)
            setIsLoading(false)
            setHasError(false)
            return
        }

        if (cache[cacheKey] !== undefined) {
            setData(cache[cacheKey] as T)
            setIsLoading(false)
            setHasError(false)
        } else {
            setIsLoading(true)
            setHasError(false)
        }

        const id = ++fetchIdRef.current
        fetcherRef.current().then(result => {
            if (id !== fetchIdRef.current) return
            cache[cacheKey] = result
            setData(result)
            setHasError(false)
            setIsLoading(false)
        }).catch(() => {
            if (id !== fetchIdRef.current) return
            setHasError(true)
            setIsLoading(false)
        })
    }, [cacheKey])

    const refetch = useCallback(() => {
        if (!cacheKey) return
        const hasCachedData = cache[cacheKey] !== undefined
        const id = ++fetchIdRef.current
        if (!hasCachedData || options.reportRefreshErrors) {
            setIsLoading(true)
            setHasError(false)
        }
        fetcherRef.current().then(result => {
            if (id !== fetchIdRef.current) return
            cache[cacheKey] = result
            setData(result)
            setHasError(false)
            setIsLoading(false)
        }).catch(() => {
            if (id !== fetchIdRef.current) return
            if (!hasCachedData || options.reportRefreshErrors) {
                setHasError(true)
                setIsLoading(false)
            }
        })
    }, [cacheKey, options.reportRefreshErrors])

    const silentRefetch = useCallback(async (shouldApply?: (result: T) => boolean) => {
        if (!cacheKey) return
        const id = ++fetchIdRef.current
        try {
            const result = await fetcherRef.current()
            if (id !== fetchIdRef.current) return
            if (shouldApply && !shouldApply(result)) return result
            cache[cacheKey] = result
            setData(result)
            return result
        } catch {
            // Background refreshes keep the last confirmed view and retry later.
        }
    }, [cacheKey])

    useEffect(() => {
        if (!cacheKey || !options.refreshOnDropdownOpen) return

        const handleDropdownOpen = (event: Event) => {
            if (options.refreshOnDropdownOpen === true) {
                refetch()
                return
            }
            const eventScope = (event as CustomEvent<DropdownOpenDetail>).detail?.scope
            if (eventScope === options.refreshOnDropdownOpen) {
                refetch()
            }
        }

        window.addEventListener(DROPDOWN_OPEN_EVENT, handleDropdownOpen)
        return () => {
            window.removeEventListener(DROPDOWN_OPEN_EVENT, handleDropdownOpen)
        }
    }, [cacheKey, options.refreshOnDropdownOpen, refetch])

    return { data, isLoading, hasError, refetch, silentRefetch }
}
